import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { FolderPlus, Send, Users, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BackBtn from "../widgets/BackBtn";
import { getErrorResponse, getHeader } from "../utils/util";
import { API_ENDPOINT } from "../utils/constants";
import { toast } from "react-toastify";
import useApiEp from "../hooks/useApiEp";

const AddCampaignGroup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [contactList, setContactList] = useState(null);
  const [isDbMode, setIsDbMode] = useState(false); // Toggle state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState("");
  const [contactListError, setContactListError] = useState("");

  // Fetch Contact Lists
  const {
    data: groupListData,
    isLoading: groupListLoading,
    isError: groupListFetchError,
  } = useApiEp(`/user/group-list`);

  // Fetch Database Lists
  const {
    data: dbList,
    isLoading: dbLoading,
    isError: isDbError,
  } = useApiEp(`/user/get-my-assigned-dbs`);

  // Helper function to safely extract names from an array of objects or strings
  const extractNames = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return list.map((item) =>
      typeof item === "object" && item !== null ? item.name : item
    );
  };

  // Dynamic values based on selected mode
  const currentOptions = isDbMode
    ? extractNames(dbList)
    : extractNames(groupListData);
  const currentLoading = isDbMode ? dbLoading : groupListLoading;
  const currentFetchError = isDbMode ? isDbError : groupListFetchError;
  const currentLabel = isDbMode ? "Database List" : "Contact List";
  const CurrentIcon = isDbMode ? Database : Users;

  const validate = () => {
    let valid = true;

    if (!name.trim()) {
      setNameError("Group name is required");
      valid = false;
    } else if (name.trim().length < 3) {
      setNameError("Name must be at least 3 characters");
      valid = false;
    } else {
      setNameError("");
    }

    if (!contactList) {
      setContactListError(`${currentLabel} is required`);
      valid = false;
    } else {
      setContactListError("");
    }

    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/campaign/create-campaign-group`,
        {
          name: name.trim(),
          contactList,
          isDb: isDbMode,
        },
        getHeader()
      );
      toast.success(response.data.message);
      navigate(-1);
    } catch (err) {
      setError(getErrorResponse(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const handleModeChange = (e) => {
    setIsDbMode(e.target.value === "true");
    setContactList(null); // Reset selection when switching lists
    setContactListError("");
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 3, pb: 6 }}>
      <BackBtn />
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, mb: 4 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: 2,
            flexShrink: 0,
          }}
        >
          <FolderPlus size={22} strokeWidth={2} color="white" />
        </Box>
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ letterSpacing: "-0.4px" }}
          >
            New Campaign Group
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a group to organise your campaigns
          </Typography>
        </Box>
      </Box>

      {/* Error Banner */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {currentFetchError && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          Could not load {currentLabel.toLowerCase()}s. Please refresh and try
          again.
        </Alert>
      )}

      {/* Form Card */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 3,
          bgcolor: "background.paper",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Group Name */}
        <TextField
          label="Group Name"
          placeholder="e.g. Q3 Retention Campaigns"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError("");
          }}
          onKeyDown={handleKeyDown}
          error={!!nameError}
          helperText={nameError || " "}
          fullWidth
          autoFocus
          disabled={isLoading}
          InputProps={{ sx: { borderRadius: 2 } }}
        />

        {/* Source Toggle */}
        <FormControl component="fieldset">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Data Source
          </Typography>
          <RadioGroup
            row
            value={isDbMode.toString()}
            onChange={handleModeChange}
          >
            <FormControlLabel
              value="false"
              control={<Radio size="small" />}
              label="Contact List"
              disabled={isLoading}
            />
            <FormControlLabel
              value="true"
              control={<Radio size="small" />}
              label="Database List"
              disabled={isLoading}
            />
          </RadioGroup>
        </FormControl>

        {/* Contact/Database List */}
        <Autocomplete
          options={currentOptions}
          value={contactList}
          onChange={(_, val) => {
            setContactList(val);
            if (contactListError) setContactListError("");
          }}
          loading={currentLoading}
          disabled={isLoading || currentFetchError}
          noOptionsText={`No ${currentLabel.toLowerCase()}s found`}
          isOptionEqualToValue={(opt, val) => opt === val}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CurrentIcon size={14} strokeWidth={2} />
                  <span>{currentLabel}</span>
                  <Typography
                    component="span"
                    sx={{ color: "error.main", ml: 0.2, lineHeight: 1 }}
                  >
                    *
                  </Typography>
                </Box>
              }
              placeholder={`Search and select a ${currentLabel.toLowerCase()}…`}
              error={!!contactListError}
              helperText={contactListError || " "}
              InputProps={{
                ...params.InputProps,
                sx: { borderRadius: 2 },
                endAdornment: (
                  <>
                    {currentLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          fullWidth
        />

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            disabled={isLoading}
            onClick={() => navigate(-1)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            fullWidth
            disabled={isLoading || !name.trim() || !contactList}
            onClick={handleSubmit}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Send size={16} strokeWidth={2} />
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: 2,
            }}
          >
            {isLoading ? "Creating…" : "Create Group"}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default AddCampaignGroup;
